import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '@components/ui/dialog';
import { Button } from '@components/ui/button';
import { useConfirmStore } from '@/stores/useConfirm';
import { AlertTriangle, CheckCircle, Info, Trash2 } from 'lucide-react';

/**
 * ConfirmDialog component that provides a reusable confirmation dialog
 * 
 * This component should be placed once in your app root or layout component
 * to handle all confirmation dialogs throughout the application.
 */
export function ConfirmDialog() {
  const { isOpen, config, handleConfirm, handleCancel, close } = useConfirmStore();

  if (!config) return null;

  // Get icon and styling based on variant
  const getVariantConfig = (variant: string) => {
    switch (variant) {
      case 'destructive':
        return {
          icon: Trash2,
          iconBg: 'bg-red-100 dark:bg-red-900/20',
          iconColor: 'text-red-600 dark:text-red-400',
        };
      case 'default':
        return {
          icon: CheckCircle,
          iconBg: 'bg-green-100 dark:bg-green-900/20',
          iconColor: 'text-green-600 dark:text-green-400',
        };
      case 'secondary':
        return {
          icon: Info,
          iconBg: 'bg-blue-100 dark:bg-blue-900/20',
          iconColor: 'text-blue-600 dark:text-blue-400',
        };
      default:
        return {
          icon: AlertTriangle,
          iconBg: 'bg-amber-100 dark:bg-amber-900/20',
          iconColor: 'text-amber-600 dark:text-amber-400',
        };
    }
  };

  const { icon: Icon, iconBg, iconColor } = getVariantConfig(config.variant || 'default');

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && close()}>
      <DialogContent className="sm:max-w-md p-6" showCloseButton={true}>
        <div className="flex flex-col items-center text-center space-y-4">
          {/* Large centered icon */}
          <div className={`w-16 h-16 rounded-full ${iconBg} flex items-center justify-center`}>
            <Icon className={`w-8 h-8 ${iconColor}`} />
          </div>

          {/* Title and description */}
          <div className="space-y-2">
            <DialogTitle className="text-xl font-semibold text-gray-900 dark:text-gray-100">
              {config.title}
            </DialogTitle>
            <DialogDescription className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
              {config.description}
            </DialogDescription>
          </div>
        </div>

        {/* Action buttons */}
        <DialogFooter className="flex flex-col-reverse sm:flex-row gap-3 mt-6">
          <Button 
            variant="outline" 
            onClick={handleCancel}
            className="flex-1 cursor-pointer"
          >
            {config.cancelText}
          </Button>
          <Button
            variant={config.variant}
            onClick={handleConfirm}
            autoFocus
            className="flex-1 cursor-pointer"
          >
            {config.confirmText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
} 
